import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MediaType } from "@front/m19-api-client";
import { AccountSelectionService, BrandAssetsService } from "@front/m19-services";
import { TranslocoDirective } from "@jsverse/transloco";
import { CreativePreviewAssets } from "@m19-board/shared/creative-preview.component";
import { switchMap } from "rxjs";

@Component({
  selector: "app-assets-preview",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective],
  host: {
    class: "w-full",
  },
  template: `
    <ng-container *transloco="let t">
      <div class="flex items-start gap-2.5">
        <div class="size-14 shrink-0 overflow-hidden rounded-xl border p-1" [class.bg-muted]="!assets()?.logoAssetId">
          <img [src]="effectiveLogoAssetUrl()" alt="Logo" class="size-full object-contain" />
        </div>
        <div class="flex flex-col gap-1 py-1.5">
          @if (assets()?.headline) {
            <div>
              {{ assets()?.headline }}
            </div>
          } @else {
            <div class="bg-muted w-40 rounded-lg">
              <span class="text-transparent">placeholder</span>
            </div>
          }
          @if (assets()?.brandName) {
            <div class="type-label text-blue-500">
              {{ t("creative-preview-list.shop_brand", [assets()?.brandName]) }}
            </div>
          } @else {
            <div class="bg-muted type-label w-20 rounded-lg text-transparent">placeholder</div>
          }
        </div>
      </div>
    </ng-container>
  `,
})
export class AssetsPreviewComponent {
  readonly brandAssetsService = inject(BrandAssetsService);
  readonly accountSelectionService = inject(AccountSelectionService);

  readonly assets = input<CreativePreviewAssets>();

  private readonly brandAssets = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.brandAssetsService.getBrandAssetsById(am.accountId, am.marketplace)),
    ),
  );

  readonly logoAsset = computed(() => {
    const brandEntityId = this.assets()?.brandEntityId;
    return this.brandAssets()
      ?.get(this.assets()?.logoAssetId ?? "")
      ?.filter((ba) => brandEntityId == ba.brandEntityId || (brandEntityId == null && ba.brandEntityId === ""))
      ?.find((ba) => ba.mediaType === MediaType.brandLogo);
  });

  readonly effectiveLogoAssetUrl = computed(() => {
    const logoAssetUrl = this.logoAsset()?.url;
    return logoAssetUrl ?? this.assets()?.logoAssetId ?? "assets/img/img_placeholder.svg";
  });
}
